import React, { useState, useEffect } from 'react';
import { BugReport, BugReportFormData } from '../../types/bugReport';
import { 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Card, 
  CardContent, 
  CardHeader, 
  Typography,
  Box,
  IconButton,
  Paper,
  Stack,
  Chip,
  Avatar
} from '@mui/material';
import { 
  Upload, 
  Delete, 
  BugReport as BugIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachFile as AttachmentIcon
} from '@mui/icons-material';

interface BugReportFormProps {
  onSubmit: (data: BugReportFormData, file?: File) => void;
  onCancel?: () => void;
  initialData?: BugReport | null;
}

export function BugReportForm({ onSubmit, onCancel, initialData }: BugReportFormProps) {
  const [formData, setFormData] = useState<BugReportFormData>({
    title: '',
    description: '',
    status: 'open',
    priority: 'medium',
    reporter: '', 
    pageUrl: '', 
    attachments: [],
  });
  
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title,
        description: initialData.description,
        status: initialData.status,
        priority: initialData.priority,
        reporter: initialData.reporter,
        pageUrl: initialData.pageUrl || '',
        attachments: initialData.attachments || [],
      });
      // Note: We can't restore the original file object, only show the filename
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData, attachmentFile || undefined);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAttachmentFile(file);
      // Update attachments array with filename for display
      setFormData({
        ...formData,
        attachments: [file.name]
      });
    }
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setFormData({
      ...formData,
      attachments: []
    });
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'open':
        return { backgroundColor: '#EBF8FF', color: '#1E40AF', border: '1px solid #BFDBFE' };
      case 'in-progress':
        return { backgroundColor: '#FEF3C7', color: '#D97706', border: '1px solid #FDE68A' };
      case 'resolved':
        return { backgroundColor: '#D1FAE5', color: '#059669', border: '1px solid #A7F3D0' };
      case 'closed':
        return { backgroundColor: '#F3F4F6', color: '#6B7280', border: '1px solid #D1D5DB' };
      default:
        return { backgroundColor: '#F3F4F6', color: '#6B7280', border: '1px solid #D1D5DB' };
    }
  };

  const getPriorityStyle = (priority: string) => {
    switch (priority) {
      case 'low':
        return { backgroundColor: '#F0F9FF', color: '#0369A1', border: '1px solid #BAE6FD' };
      case 'medium':
        return { backgroundColor: '#FFFBEB', color: '#D97706', border: '1px solid #FED7AA' };
      case 'high':
        return { backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' };
      case 'critical':
        return { backgroundColor: '#FDF2F8', color: '#BE185D', border: '1px solid #FBCFE8' };
      default:
        return { backgroundColor: '#F3F4F6', color: '#6B7280', border: '1px solid #D1D5DB' };
    }
  };

  return (
    <Box sx={{ maxWidth: { xs: '100%', md: '900px' }, mx: 'auto', width: '100%' }}>
      <Card 
        elevation={0}
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          maxWidth: '100%',
          width: '100%',
        }}
      >
        {/* Header with gradient background */}
        <Box sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e40af 100%)',
          color: '#ffffff',
          p: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BugIcon sx={{ fontSize: 32, color: '#ffffff' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#ffffff' }}>
                {initialData ? 'Hata Raporunu Düzenle' : 'Yeni Hata Raporu'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, color: '#ffffff' }}>
                {initialData ? 'Mevcut hata raporunu güncelleyin' : 'Karşılaştığınız hatayı detaylı olarak bildirin'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <CardContent sx={{ p: 1.5 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Title Field */}
            <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <TextField
                label="Başlık"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Hatanın kısa ve açıklayıcı başlığı"
                required
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1e40af',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1e40af',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#1e40af',
                  },
                }}
              />
            </Paper>

            {/* Description Field */}
            <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <TextField
                label="Açıklama"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Hatanın detaylı açıklaması, ne zaman oluştuğu, hangi adımları takip ettiğiniz..."
                required
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1e40af',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1e40af',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#1e40af',
                  },
                }}
              />
            </Paper>

            {/* Status and Priority */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#1e40af' } }}>Durum</InputLabel>
                  <Select
                    value={formData.status}
                    label="Durum"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as BugReport['status'] })}
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1e40af',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1e40af',
                      },
                    }}
                  >
                    <MenuItem value="open">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" label="Açık" sx={getStatusStyle('open')} />
                      </Box>
                    </MenuItem>
                    <MenuItem value="in-progress">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" label="Devam Ediyor" sx={getStatusStyle('in-progress')} />
                      </Box>
                    </MenuItem>
                    <MenuItem value="resolved">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" label="Çözüldü" sx={getStatusStyle('resolved')} />
                      </Box>
                    </MenuItem>
                    <MenuItem value="closed">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" label="Kapalı" sx={getStatusStyle('closed')} />
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Paper>

              <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#1e40af' } }}>Öncelik</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Öncelik"
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as BugReport['priority'] })}
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1e40af',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1e40af',
                      },
                    }}
                  >
                    <MenuItem value="low">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" label="Düşük" sx={getPriorityStyle('low')} />
                      </Box>
                    </MenuItem>
                    <MenuItem value="medium">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" label="Orta" sx={getPriorityStyle('medium')} />
                      </Box>
                    </MenuItem>
                    <MenuItem value="high">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" label="Yüksek" sx={getPriorityStyle('high')} />
                      </Box>
                    </MenuItem>
                    <MenuItem value="critical">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" label="Kritik" sx={getPriorityStyle('critical')} />
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Paper>
            </Box>

            {/* Page URL Field */}
            <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <TextField
                label="Sayfa URL (hatanın oluştuğu sayfa)"
                type="url"
                value={formData.pageUrl}
                onChange={(e) => setFormData({ ...formData, pageUrl: e.target.value })}
                placeholder="https://example.com/sayfa"
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1e40af',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1e40af',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#1e40af',
                  },
                }}
              />
            </Paper>

            {/* File Upload */}
            <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <AttachmentIcon sx={{ color: '#1e40af' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Ek Dosya
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#1e40af',
                    color: '#1e40af',
                    '&:hover': {
                      borderColor: '#1e40af',
                      bgcolor: '#f0f4ff'
                    }
                  }}
                >
                  Dosya Seç
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Resim, PDF, DOC veya TXT dosyası yükleyebilirsiniz
                </Typography>
              </Box>
              
              {/* Show current attachment */}
              {(attachmentFile || (formData.attachments && formData.attachments.length > 0)) && (
                <Box sx={{ mt: 2 }}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      p: 1.5, 
                      bgcolor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e40af 100%)', color: '#ffffff', width: 40, height: 40 }}>
                        <AttachmentIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {attachmentFile ? attachmentFile.name : formData.attachments?.[0]}
                        </Typography>
                        {attachmentFile && (
                          <Typography variant="caption" color="text.secondary">
                            {(attachmentFile.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <IconButton 
                      onClick={removeAttachment}
                      sx={{ 
                        color: '#ef4444',
                        '&:hover': { bgcolor: '#fef2f2' }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Paper>
                </Box>
              )}
            </Paper>

            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 1.5,
              pt: 1.5,
              borderTop: '1px solid #e2e8f0'
            }}>
              {onCancel && (
                <Button 
                  variant="outlined" 
                  onClick={onCancel}
                  startIcon={<CancelIcon />}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                    '&:hover': {
                      borderColor: '#9ca3af',
                      bgcolor: '#f9fafb'
                    }
                  }}
                >
                  İptal
                </Button>
              )}
              <Button 
                type="submit" 
                variant="contained"
                startIcon={<SaveIcon />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)'
                  }
                }}
              >
                {initialData ? 'Hata Raporunu Güncelle' : 'Hata Raporunu Gönder'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
} 